import { IMinerNameProps } from "./MinerName.props";
import cn from "classnames";
import styles from "./MinerName.module.scss";
import { useMinerNameControl } from "./useMinerNameControl";
import Copy from "../Copy/Copy";
import { Divider, Tooltip } from "antd";
import Icon from "../Icon/Icon";
import { useIntl } from "react-intl";
import ports_icon from "@assets/icons/ports.svg";
import copy from "@assets/redesign/icons/copy.svg";
import { IReproxyLink } from "@services/getMinerList";
import SmallLink from "../SmallLink/SmallLink";
import MinerDataPopover3 from "@mobx/components/MinerDataPopover3";

interface MinerNameReproxyLinkProps {
  readonly minerId: number;
  readonly link?: IReproxyLink;
  onDoPortClicked: (minerId: number, prevPort: number) => void;
  onDeletePortClicked: (minerId: number, port: number) => void;
}

function MinerNameReproxyLink({
  minerId,
  link,
  onDoPortClicked,
  onDeletePortClicked,
}: MinerNameReproxyLinkProps): JSX.Element {

  const { formatMessage } = useIntl();

  return (
    <div className={styles.port_content}>
      {link === undefined && (
        <Icon
          className={styles.ports_icon}
          src={ports_icon}
          title={formatMessage({ id: "miners.ports.access_miner" })}
          onClick={() => {
            onDoPortClicked(minerId, -1);
          }}
        />
      )}
      {link && link.Port > 0 && (<>
        <SmallLink
          onClick={() => {
            onDoPortClicked(minerId, link.Port);
            // if (link && link.Port > 0) {
            //   onPortClick(link.Port);
            // }
          }}>
          <span className={`${styles.port_text}`} title={link.Port.toString()}>[{link.Port}]</span>
        </SmallLink>
        <SmallLink
          className={`red_bold ${styles.close_port}`}
          onClick={() => {
            if (link && link.Port > 0) {
              onDeletePortClicked(minerId, link.Port);
            }
          }}
        >X</SmallLink>
      </>)}
    </div>
  );
}

export default function MinerName({
  className,
  fullAddress,
  ...props
}: IMinerNameProps) {

  const control = useMinerNameControl(props);

  return (
    <Tooltip
      open={control.isCrop ? undefined : false}
      trigger={"hover"}
      title={<p className={styles.tooltipName}>{control.name}</p>}
      color='#000'
    >
      <div
        id={`md_${props.minerId}`}
        className={cn(styles.wrapper, {
          [styles.single]: !props.canCopy,
        })}
        onMouseEnter={() => control.onShowCopy(true)}
        onMouseLeave={() => control.onShowCopy(false)}
      >
        <h5
          className={cn(styles.title, className, {
            [styles.crop]: control.isCrop,
            [styles.smallerFont]:
              !props.useFullName && control.isChangeFontSize,
          })}
          title={props.children}
        >
          {control.name}
        </h5>

        {props.canCopy && props.minerId && (
          <MinerDataPopover3
            iconSrc={copy}
            className={cn(styles.copy_icon, {
              [styles.show]: control.showCopy,
            })}
            showCopy={control.showCopy}
            loader={control.loader}
            disabledIp={(control.minerData?.ip?.length || 0) < 1}
            disabledMac={(control.minerData?.mac?.length || 0) < 1}
            disabledPass={(control.minerData?.password?.length || 0) < 1}
            placement={props.copyPopoverPlacement}
            onOpenChange={control.onLoadMinerData}
            onCopyData={control.onCopyData}
            reproxyContent={
              <>
                <Divider type="horizontal" style={{
                  paddingBottom: 0,
                  paddingTop: 0,
                  margin: 0,
                }} />
                <div style={{
                  position: "relative",
                  paddingBottom: 18,
                  width: "100%"
                }}>
                  <MinerNameReproxyLink
                    minerId={props.minerId}
                    link={control.reproxyLink}
                    onDoPortClicked={control.createPortAndOpen}
                    onDeletePortClicked={control.deleteMinerPort}
                  />
                </div>
              </>
            }
          />
        )}
        {props.canCopy && !props.minerId && (
          <Copy
            text={fullAddress || props.children}
            className={cn(styles.copy_icon, {
              [styles.show]: control.showCopy,
            })}
            icon={copy}
          />
        )}
      </div>
    </Tooltip>
  );
}
